import * as eventemitter3 from 'eventemitter3';
const isDatachannelReady = (dataChannel) => dataChannel.readyState === 'open';
export default class AveDataChannel extends eventemitter3.EventEmitter {
    datachannel;
    logger;
    constructor(datachannel, logger) {
        super();
        this.datachannel = datachannel;
        this.logger = logger;
    }
    static open(peerConnection, props) {
        const logger = props.logger;
        try {
            return new Promise((resolve, reject) => {
                logger.debug('Creating new datachannel');
                const datachannel = peerConnection.createDataChannel('AVE', { ordered: true });
                const aveDataChannel = new AveDataChannel(datachannel, logger);
                datachannel.binaryType = 'arraybuffer';
                datachannel.onopen = () => {
                    logger.debug('Datachannel opened');
                    if (props.dataChannelOnly) {
                        logger.debug('In datachannel-only mode -> not requesting quality control');
                    }
                    else {
                        logger.debug('Requesting quality control');
                        aveDataChannel.send({ id: AveControlMessageType.RequestQualityControl, data: [] });
                    }
                    datachannel.onopen = null;
                    datachannel.onclose = () => aveDataChannel.emit('close');
                    datachannel.onerror = (event) => aveDataChannel.emit('error', event.error);
                    datachannel.onmessage = (messageEvent) => {
                        aveDataChannel.handleMessage(messageEvent.data);
                    };
                    resolve(aveDataChannel);
                };
                datachannel.onclose = (event) => {
                    logger.debug('Datachannel closed', { readyState: datachannel.readyState, event });
                    datachannel.onopen = null;
                    datachannel.onclose = null;
                    reject(new Error(`Could not establish data channel. State ${datachannel.readyState}`));
                };
            });
        }
        catch (error) {
            logger.error('Error while initializing datachannel', error);
            throw error;
        }
    }
    close() {
        this.logger.debug('Closing datachannel', { readyState: this.datachannel.readyState });
        this.datachannel.close();
    }
    closeAsync() {
        if (isDatachannelReady(this.datachannel)) {
            return new Promise((resolve, reject) => {
                setTimeout(() => reject(new Error('Datachannel did not close in time')), 5000);
                this.datachannel.onclose = () => {
                    this.emit('close');
                    this.logger.debug('Datachannel closed', { readyState: this.datachannel.readyState });
                    resolve();
                };
                this.close();
            }).finally(() => {
                this.datachannel.onclose = null;
            });
        }
        this.close();
        return Promise.resolve();
    }
    handleMessage(data) {
        // the first byte of data determines what type of message it is, the rest of the data is the payload
        const byteArray = new Uint8Array(data);
        const messageType = byteArray[0];
        if (messageType === ClientMessageType.Response) {
            // do not log here to prevent log spam
            const responseString = new TextDecoder('utf-16').decode(byteArray.slice(1));
            const responseJson = JSON.parse(responseString);
            this.emit('response', responseJson);
        }
        else {
            logUnhandledMessage(data, this.logger);
        }
    }
    sendAveCommand(request) {
        this.send(toUiInteractionMessage(request));
    }
    send(aveControlMessage) {
        if (!isDatachannelReady(this.datachannel)) {
            return;
        }
        const message = serializeMessage(aveControlMessage, this.logger);
        this.datachannel.send(message);
    }
}
function toUiInteractionMessage(message) {
    return {
        id: AveControlMessageType.UIInteraction,
        data: [{ type: 'string', payload: JSON.stringify(message) }]
    };
}
function calculateByteLength(data, logger) {
    // start with 1 byte for the message id, then add more bytes for different payloads
    let bufferByteLength = 1;
    data.forEach((entry) => {
        switch (entry.type) {
            case 'uint8':
                bufferByteLength += 1;
                break;
            case 'int16':
            case 'uint16':
                bufferByteLength += 2;
                break;
            case 'string':
                // 2 bytes for payload length
                bufferByteLength += 2;
                // 2 bytes per character of payload
                bufferByteLength += 2 * entry.payload.length;
                break;
            default:
                checkEnumExhaust(entry, logger);
        }
    });
    return bufferByteLength;
}
function checkEnumExhaust(x, logger) {
    try {
        logger.error(`Encountered unsupported enum=${JSON.stringify(x)}`);
    }
    catch (error) {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        logger.error(`Encountered unsupported enum=${x}`);
    }
}
function serializeMessage(aveControlMessage, logger) {
    // the Unreal Engine expects each message to start with the message identifier, followed by the message payload
    const byteLength = calculateByteLength(aveControlMessage.data, logger);
    const dataView = new DataView(new ArrayBuffer(byteLength));
    dataView.setUint8(0, aveControlMessage.id);
    let byteOffset = 1;
    aveControlMessage.data.forEach((entry) => {
        switch (entry.type) {
            case 'uint8':
                dataView.setUint8(byteOffset, entry.payload);
                byteOffset += 1;
                break;
            case 'int16':
                dataView.setInt16(byteOffset, entry.payload, true);
                byteOffset += 2;
                break;
            case 'uint16':
                dataView.setUint16(byteOffset, entry.payload, true);
                byteOffset += 2;
                break;
            case 'string':
                // for string messages the Unreal Engine expects the payload length in the first 2 bytes
                dataView.setUint16(byteOffset, entry.payload.length, true);
                byteOffset += 2;
                // for string messages the Unreal Engine expects the payload as separate characters 2 bytes at a time
                for (let i = 0; i < entry.payload.length; i++) {
                    dataView.setUint16(byteOffset, entry.payload.charCodeAt(i), true);
                    byteOffset += 2;
                }
                break;
            default:
                checkEnumExhaust(entry, logger);
        }
    });
    return dataView.buffer;
}
/*
 * Messages that the Unreal Engine sends to the client,
 * see https://github.com/EpicGames/PixelStreamingInfrastructure/blob/UE5.3/Frontend/library/src/UeInstanceMessage/StreamMessageController.ts#L182
 */
var ClientMessageType;
(function (ClientMessageType) {
    ClientMessageType[ClientMessageType["QualityControlOwnership"] = 0] = "QualityControlOwnership";
    ClientMessageType[ClientMessageType["Response"] = 1] = "Response";
    ClientMessageType[ClientMessageType["Command"] = 2] = "Command";
    ClientMessageType[ClientMessageType["FreezeFrame"] = 3] = "FreezeFrame";
    ClientMessageType[ClientMessageType["UnfreezeFrame"] = 4] = "UnfreezeFrame";
    ClientMessageType[ClientMessageType["VideoEncoderAvgQP"] = 5] = "VideoEncoderAvgQP";
    ClientMessageType[ClientMessageType["LatencyTest"] = 6] = "LatencyTest";
    ClientMessageType[ClientMessageType["InitialSettings"] = 7] = "InitialSettings";
    ClientMessageType[ClientMessageType["FileExtension"] = 8] = "FileExtension";
    ClientMessageType[ClientMessageType["FileMimeType"] = 9] = "FileMimeType";
    ClientMessageType[ClientMessageType["FileContents"] = 10] = "FileContents";
    ClientMessageType[ClientMessageType["TestEcho"] = 11] = "TestEcho";
    ClientMessageType[ClientMessageType["InputControlOwnership"] = 12] = "InputControlOwnership";
    ClientMessageType[ClientMessageType["Protocol"] = 255] = "Protocol";
})(ClientMessageType || (ClientMessageType = {}));
/*
 * Messages that the client can send to the Unreal Engine,
 * see https://github.com/EpicGames/PixelStreamingInfrastructure/blob/UE5.3/Frontend/library/src/UeInstanceMessage/StreamMessageController.ts#L42
 */
export var AveControlMessageType;
(function (AveControlMessageType) {
    /* Control Messages. Range = 0..49. */
    AveControlMessageType[AveControlMessageType["IFrameRequest"] = 0] = "IFrameRequest";
    AveControlMessageType[AveControlMessageType["RequestQualityControl"] = 1] = "RequestQualityControl";
    AveControlMessageType[AveControlMessageType["FpsRequest"] = 2] = "FpsRequest";
    AveControlMessageType[AveControlMessageType["AverageBitrateRequest"] = 3] = "AverageBitrateRequest";
    AveControlMessageType[AveControlMessageType["StartStreaming"] = 4] = "StartStreaming";
    AveControlMessageType[AveControlMessageType["StopStreaming"] = 5] = "StopStreaming";
    AveControlMessageType[AveControlMessageType["LatencyTest"] = 6] = "LatencyTest";
    AveControlMessageType[AveControlMessageType["RequestInitialSettings"] = 7] = "RequestInitialSettings";
    AveControlMessageType[AveControlMessageType["TestEcho"] = 8] = "TestEcho";
    /* Input Messages. Range = 50..89. */
    // Generic Input Messages. Range = 50..59.
    AveControlMessageType[AveControlMessageType["UIInteraction"] = 50] = "UIInteraction";
    AveControlMessageType[AveControlMessageType["Command"] = 51] = "Command";
    // Keyboard Input Message. Range = 60..69.
    AveControlMessageType[AveControlMessageType["KeyDown"] = 60] = "KeyDown";
    AveControlMessageType[AveControlMessageType["KeyUp"] = 61] = "KeyUp";
    AveControlMessageType[AveControlMessageType["KeyPress"] = 62] = "KeyPress";
    // Mouse Input Messages. Range = 70..79.
    AveControlMessageType[AveControlMessageType["MouseEnter"] = 70] = "MouseEnter";
    AveControlMessageType[AveControlMessageType["MouseLeave"] = 71] = "MouseLeave";
    AveControlMessageType[AveControlMessageType["MouseDown"] = 72] = "MouseDown";
    AveControlMessageType[AveControlMessageType["MouseUp"] = 73] = "MouseUp";
    AveControlMessageType[AveControlMessageType["MouseMove"] = 74] = "MouseMove";
    AveControlMessageType[AveControlMessageType["MouseWheel"] = 75] = "MouseWheel";
    // Touch Input Messages. Range = 80..89.
    AveControlMessageType[AveControlMessageType["TouchStart"] = 80] = "TouchStart";
    AveControlMessageType[AveControlMessageType["TouchEnd"] = 81] = "TouchEnd";
    AveControlMessageType[AveControlMessageType["TouchMove"] = 82] = "TouchMove";
    // Gamepad Input Messages. Range = 90..99
    AveControlMessageType[AveControlMessageType["GamepadButtonPressed"] = 90] = "GamepadButtonPressed";
    AveControlMessageType[AveControlMessageType["GamepadButtonReleased"] = 91] = "GamepadButtonReleased";
    AveControlMessageType[AveControlMessageType["GamepadAnalog"] = 92] = "GamepadAnalog";
})(AveControlMessageType || (AveControlMessageType = {}));
function logUnhandledMessage(data, logger) {
    const byteArray = new Uint8Array(data);
    const messageType = byteArray[0];
    switch (messageType) {
        case ClientMessageType.QualityControlOwnership:
            // eslint-disable-next-line no-case-declarations
            const ownership = byteArray[1] !== 0;
            logger.debug(`Datachannel received quality control ownership message, changed to ${ownership}`);
            break;
        case ClientMessageType.Command:
            (() => {
                const commandAsString = new TextDecoder('utf-16').decode(data.slice(1));
                logger.debug(`Datachannel received command ${commandAsString}`);
                logger.debug('Command messages are not yet supported.');
            })();
            break;
        case ClientMessageType.InitialSettings:
            (() => {
                const settingsString = new TextDecoder('utf-16').decode(data.slice(1));
                logger.debug('Datachannel received initial settings', { settingsString });
            })();
            break;
        case ClientMessageType.Protocol:
            (() => {
                const protocolString = new TextDecoder('utf-16').decode(data.slice(1));
                const protocolJSON = JSON.parse(protocolString);
                logger.debug('Datachannel received protocol', protocolJSON);
                logger.debug('Protocol messages are not yet supported.');
            })();
            break;
        case ClientMessageType.FileExtension:
            logger.debug('FileExtension messages are not yet supported.');
            break;
        case ClientMessageType.FileMimeType:
            logger.debug('FileMimeType messages are not yet supported.');
            break;
        case ClientMessageType.FileContents:
            logger.debug('FileContent messages are not yet supported.');
            break;
        case ClientMessageType.VideoEncoderAvgQP:
            // This is spammed by AVE, therefore we drop it silently
            break;
        case ClientMessageType.InputControlOwnership:
            logger.debug('InputControlOwnership messages are not yet supported.');
            break;
        case ClientMessageType.FreezeFrame:
            logger.debug('FreezeFrame messages are not supported anymore.');
            break;
        case ClientMessageType.UnfreezeFrame:
            logger.debug('UnfreezeFrame messages are not supported anymore.');
            break;
        default:
            logger.error(`Datachannel received unrecognized data, packet ID = ${messageType}`);
            break;
    }
}
//# sourceMappingURL=ave-data-channel.js.map